/*
 * AdminSettings — central admin profile + security + platform config.
 *
 * Tabs:
 *   1. Profile     — name / email / phone / password change
 *   2. Security    — 2FA enrolment · recovery codes · trusted devices · sessions
 *   3. Hardening   — IP allowlist · 2FA enforcement · session TTL · magic link
 *   4. Anomalies   — editable thresholds for the override-anomaly engine
 *   5. Audit       — admin login log + own override history
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { TwoFactorPanel } from "../../components/SettingsPanels";
import {
  Shield, KeyRound, Fingerprint, Globe, Activity, AlertTriangle,
  ScrollText, Trash2, Copy, RefreshCw, CheckCircle2, XCircle,
  Wifi, Clock, Loader2, UserCog, Plus, Sparkles, Users, Mail, Edit3,
  Save, ArrowUp, ArrowDown, Crown, Send,
} from "lucide-react";


const TABS = [
  { id: "profile",   label: "Profile",        icon: UserCog },
  { id: "team",      label: "Admin team",     icon: Users },
  { id: "security",  label: "Security",       icon: Shield },
  { id: "hardening", label: "Admin hardening", icon: Wifi },
  { id: "anomalies", label: "Anomaly thresholds", icon: AlertTriangle },
  { id: "audit",     label: "Login audit",     icon: ScrollText },
  { id: "useraudit", label: "User audit chain", icon: Shield },
  { id: "readiness", label: "Production readiness", icon: CheckCircle2 },
];


export default function AdminSettings() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = (() => {
    const q = searchParams.get("tab");
    return TABS.some(t => t.id === q) ? q : "profile";
  })();
  const [tab, setTab] = useState(initialTab);
  const [whoami, setWhoami] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [w, c] = await Promise.all([
        api.get("/admin/settings/whoami"),
        api.get("/admin/settings/config"),
      ]);
      setWhoami(w.data);
      setCfg(c.data);
    } catch (e) {
      toast.error("Failed to load admin settings");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  if (loading || !cfg || !whoami) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-foreground/40"/>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5" data-testid="admin-settings">
      <PageHeader
        title="Admin Settings"
        subtitle={`${user.email} · admin role · session from ${whoami.ip}`}
      />

      {/* Tabs */}
      <div className="flex gap-1 border-b" data-testid="admin-settings-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`admin-settings-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 transition-colors ${
                active ? "border-foreground text-foreground" : "border-transparent text-foreground/55 hover:text-foreground/80"
              }`}>
              <Icon className="w-3.5 h-3.5"/>{t.label}
            </button>
          );
        })}
      </div>

      {tab === "profile"   && <ProfileTab whoami={whoami} onRefresh={refresh}/>}
      {tab === "team"      && <TeamTab/>}
      {tab === "security"  && <SecurityTab whoami={whoami} onRefresh={refresh}/>}
      {tab === "hardening" && <HardeningTab cfg={cfg} onRefresh={refresh}/>}
      {tab === "anomalies" && <AnomaliesTab cfg={cfg} onRefresh={refresh}/>}
      {tab === "audit"     && <AuditTab/>}
      {tab === "useraudit" && <UserAuditTab/>}
      {tab === "readiness" && <ReadinessTab/>}
    </div>
  );
}


/* ─── Profile tab (editable) ─────────────────────────────────────────── */
function ProfileTab({ whoami, onRefresh }) {
  const u = whoami.user;
  const [pwOpen, setPwOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: u.name || "", phone: u.phone || "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const patch = {};
      if (form.name !== (u.name || ""))   patch.name = form.name;
      if (form.phone !== (u.phone || "")) patch.phone = form.phone;
      if (Object.keys(patch).length === 0) { setEditing(false); return; }
      await api.patch("/admin/me/profile", patch);
      toast.success("Profile updated · audit-logged");
      setEditing(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <Card title="Profile">
        {editing ? (
          <div className="space-y-2">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="profile-name-input"/>
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+1 555-..." data-testid="profile-phone-input"/>
            </div>
            <Kv label="Email" value={<span className="font-mono">{u.email}</span>}/>
            <div className="text-[10.5px] text-foreground/55 -mt-1 mb-1 pl-[140px]">
              <button className="text-violet-700 hover:underline" onClick={() => setEmailOpen(true)} data-testid="profile-change-email">Change email →</button>
            </div>
            <div className="pt-3 flex gap-2">
              <Button onClick={save} disabled={busy} data-testid="profile-save">
                {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}
                <Save className="w-3.5 h-3.5 mr-1"/>Save
              </Button>
              <Button variant="ghost" onClick={() => { setEditing(false); setForm({ name: u.name || "", phone: u.phone || "" }); }}>Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            <Kv label="Name"  value={u.name || <span className="text-foreground/40">— not set</span>}/>
            <Kv label="Email" value={u.email} mono/>
            <Kv label="Phone" value={u.phone || <span className="text-foreground/40">— not set</span>}/>
            <Kv label="Role"  value={<span className="chip bg-foreground/5 border-border">{u.role}</span>}/>
            <Kv label="User ID" value={u.id} mono/>
            <div className="pt-3 flex gap-2">
              <Button onClick={() => setEditing(true)} data-testid="profile-edit">
                <Edit3 className="w-3.5 h-3.5 mr-1"/>Edit profile
              </Button>
              <Button variant="outline" onClick={() => setEmailOpen(true)} data-testid="profile-change-email-btn">
                <Mail className="w-3.5 h-3.5 mr-1"/>Change email
              </Button>
              <Button variant="outline" onClick={() => setPwOpen(true)} data-testid="settings-change-password">
                <KeyRound className="w-3.5 h-3.5 mr-1"/>Change password
              </Button>
            </div>
          </>
        )}
      </Card>
      <PasswordChangeDialog open={pwOpen} onClose={() => setPwOpen(false)}/>
      <EmailChangeDialog open={emailOpen} onClose={() => setEmailOpen(false)} currentEmail={u.email}/>
    </div>
  );
}


function EmailChangeDialog({ open, onClose, currentEmail }) {
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!newEmail || !newEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/me/email/verify-start", { new_email: newEmail });
      toast.success(`Verification email sent to ${r.data.new_email} · expires in ${r.data.expires_in_minutes} min`);
      setSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start email change");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && (onClose(), setSent(false), setNewEmail(""))}>
      <DialogContent className="max-w-md" data-testid="email-change-dialog">
        <DialogHeader>
          <DialogTitle>Change admin email</DialogTitle>
          <DialogDescription>
            We'll email a verification link to the NEW address. Click it to confirm the change. Current: <span className="font-mono">{currentEmail}</span>
          </DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="text-center py-6 space-y-2">
            <Mail className="w-8 h-8 mx-auto text-emerald-600"/>
            <div className="font-semibold">Check your inbox</div>
            <div className="text-[12px] text-foreground/65">Click the verification link in the email we just sent.</div>
          </div>
        ) : (
          <div className="space-y-2">
            <Label>New email <span className="text-rose-600">*</span></Label>
            <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="you@yourcompany.com" data-testid="email-change-input"/>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => { onClose(); setSent(false); setNewEmail(""); }}>Close</Button>
          {!sent && (
            <Button onClick={submit} disabled={busy || !newEmail} data-testid="email-change-submit">
              {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}<Send className="w-3.5 h-3.5 mr-1"/>Send verification
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Admin Team tab ─────────────────────────────────────────────────── */
function TeamTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // {kind, user}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/team");
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decide = async () => {
    if (!confirm) return;
    const { kind, user, reason } = confirm;
    if (!reason || reason.trim().length < 3) { toast.error("Reason required (min 3 chars)"); return; }
    try {
      await api.post(`/admin/team/${user.id}/${kind}`, { reason: reason.trim() });
      toast.success(`${kind === "promote" ? "Promoted" : "Demoted"} · audit-logged`);
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${kind} failed`);
    }
  };

  if (loading || !data) return <div className="flex items-center justify-center py-12"><Loader2 className="w-4 h-4 animate-spin text-foreground/40"/></div>;

  return (
    <div className="space-y-4" data-testid="admin-team-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Admin team ({data.count})</h3>
          <p className="text-[11.5px] text-foreground/60 mt-0.5">Every user with role=admin · invite new admins via email magic-link · 4-eyes for promotions and revocations.</p>
        </div>
        <Button onClick={() => setInviteOpen(true)} data-testid="team-invite-btn">
          <Plus className="w-3.5 h-3.5 mr-1"/>Invite admin
        </Button>
      </div>

      <Card title="Active admins">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-foreground/55 border-b">
              <th className="px-2 py-2 font-medium">Admin</th>
              <th className="px-2 py-2 font-medium">Tier</th>
              <th className="px-2 py-2 font-medium">Last login</th>
              <th className="px-2 py-2 font-medium">Flags</th>
              <th className="px-2 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.id} className="border-b last:border-0" data-testid={`team-row-${r.id}`}>
                <td className="px-2 py-2 text-[12px]">
                  <div className="font-medium">{r.name || r.email}</div>
                  <div className="text-[10px] text-foreground/55 font-mono">{r.email}</div>
                </td>
                <td className="px-2 py-2 text-[11px] font-mono">{r.tier}</td>
                <td className="px-2 py-2 text-[10.5px] text-foreground/65">
                  {r.last_login_at ? (
                    <>
                      <div>{new Date(r.last_login_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}</div>
                      <div className="text-[9.5px] font-mono">{r.last_login_ip || "—"}</div>
                    </>
                  ) : <span className="text-foreground/40">never</span>}
                </td>
                <td className="px-2 py-2 space-x-1">
                  {r.is_self && <span className="chip bg-emerald-100 text-emerald-700 border-emerald-200 text-[9.5px]"><Crown className="w-2.5 h-2.5 mr-0.5 inline"/>You</span>}
                  {r.is_seed && <span className="chip bg-amber-100 text-amber-700 border-amber-200 text-[9.5px]">Seed</span>}
                  {r.suspended && <span className="chip bg-rose-100 text-rose-700 border-rose-200 text-[9.5px]">Suspended</span>}
                </td>
                <td className="px-2 py-2 text-right">
                  {!r.is_self && (
                    <Button size="sm" variant="ghost" className="text-rose-700"
                            onClick={() => setConfirm({ kind: "revoke", user: r, reason: "" })}
                            data-testid={`team-revoke-${r.id}`}>
                      <ArrowDown className="w-3 h-3 mr-1"/>Demote
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {data.pending_invites.length > 0 && (
        <Card title={`Pending invites (${data.pending_invites.length})`}>
          <ul className="divide-y">
            {data.pending_invites.map(i => (
              <li key={i.id} className="py-2 text-[11.5px] flex items-center justify-between" data-testid={`invite-row-${i.id}`}>
                <div>
                  <div className="font-medium">{i.email}</div>
                  <div className="text-[10px] text-foreground/55">invited by {i.invited_by_email} · expires {new Date(i.expires_at).toLocaleDateString()}</div>
                </div>
                <span className="chip bg-amber-100 text-amber-700 border-amber-200 text-[9.5px]">pending</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <InviteAdminDialog open={inviteOpen} onClose={() => setInviteOpen(false)} onSent={load}/>
      <DemoteDialog confirm={confirm} setConfirm={setConfirm} onDecide={decide}/>
    </div>
  );
}


function InviteAdminDialog({ open, onClose, onSent }) {
  const [form, setForm] = useState({ email: "", name: "", reason: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm({ email: "", name: "", reason: "" }); }, [open]);

  const submit = async () => {
    if (!form.email || !form.email.includes("@")) { toast.error("Valid email required"); return; }
    if (form.reason.trim().length < 3) { toast.error("Reason required (min 3 chars)"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/team/invite", {
        email: form.email.trim(),
        name:  form.name.trim() || null,
        reason: form.reason.trim(),
      });
      toast.success(`Invite sent to ${form.email} · expires in 7 days`);
      onSent(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="invite-admin-dialog">
        <DialogHeader>
          <DialogTitle>Invite admin</DialogTitle>
          <DialogDescription>Sends a magic-link email · accepting upgrades the account to admin role with tier_1 quota by default.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Email <span className="text-rose-600">*</span></Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="newadmin@yourcompany.com" data-testid="invite-email"/>
          <Label>Name (optional)</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="invite-name"/>
          <Label>Reason <span className="text-rose-600">*</span></Label>
          <Textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    placeholder="Why are you adding this admin? (audit-logged)"
                    data-testid="invite-reason"/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.email || form.reason.trim().length < 3}
                  data-testid="invite-submit">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}
            <Send className="w-3.5 h-3.5 mr-1"/>Send invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function DemoteDialog({ confirm, setConfirm, onDecide }) {
  if (!confirm) return null;
  return (
    <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
      <DialogContent className="max-w-md" data-testid="team-demote-dialog">
        <DialogHeader>
          <DialogTitle>Demote {confirm.user.email}?</DialogTitle>
          <DialogDescription>
            This user will lose admin role and become a buyer. Last-admin protection is enforced server-side.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Reason <span className="text-rose-600">*</span></Label>
          <Textarea rows={3} value={confirm.reason}
                    onChange={(e) => setConfirm({ ...confirm, reason: e.target.value })}
                    data-testid="demote-reason"/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="destructive" onClick={onDecide}
                  disabled={!confirm.reason || confirm.reason.trim().length < 3}
                  data-testid="demote-submit">
            <ArrowDown className="w-3.5 h-3.5 mr-1"/>Demote
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Production Readiness tab ──────────────────────────────────────── */
function ReadinessTab() {
  const [data, setData] = useState(null);
  const [demo, setDemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleanupOpen, setCleanupOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, d] = await Promise.all([
        api.get("/admin/production-readiness"),
        api.get("/admin/demo-status"),
      ]);
      setData(r.data);
      setDemo(d.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="flex items-center justify-center py-12"><Loader2 className="w-4 h-4 animate-spin text-foreground/40"/></div>;

  const pct = data.readiness_pct;
  const tone = pct === 100 ? "emerald" : pct >= 75 ? "sky" : pct >= 50 ? "amber" : "rose";

  return (
    <div className="space-y-4" data-testid="readiness-tab">
      <Card title="Production readiness">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-3xl font-semibold tabular-nums">{pct}%</div>
            <div className="text-[11.5px] text-foreground/60">{data.passed} of {data.total} checks passed</div>
          </div>
          {data.ready ? (
            <span className="chip bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle2 className="w-3 h-3 mr-1"/>Production ready</span>
          ) : (
            <span className={`chip bg-${tone}-100 text-${tone}-700 border-${tone}-200`}>Not ready · {data.total - data.passed} pending</span>
          )}
        </div>
        <ul className="divide-y">
          {data.checks.map(c => (
            <li key={c.key} className="py-2.5 flex items-start gap-3" data-testid={`readiness-${c.key}`}>
              {c.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0"/> : <XCircle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0"/>}
              <div>
                <div className="text-[13px] font-medium">{c.label}</div>
                {!c.ok && <div className="text-[10.5px] text-foreground/60 mt-0.5">{c.hint}</div>}
              </div>
            </li>
          ))}
        </ul>
      </Card>

      {demo?.has_demo_data && (
        <Card title="Demo seed data detected">
          <p className="text-[12px] text-foreground/75 mb-3">
            These accounts were seeded for development/demo. Remove them before production launch — their data shows up in admin metrics and revenue tracking.
          </p>
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-foreground/55 border-b">
                <th className="py-1.5">Account</th>
                <th className="py-1.5">Role</th>
                <th className="py-1.5 text-right">Orders</th>
                <th className="py-1.5 text-right">Invoices</th>
                <th className="py-1.5 text-right">Wallet txns</th>
              </tr>
            </thead>
            <tbody>
              {demo.accounts.map(a => (
                <tr key={a.user_id} className="border-b last:border-0" data-testid={`demo-account-${a.role}`}>
                  <td className="py-1.5 font-mono">{a.email}</td>
                  <td className="py-1.5">{a.role}</td>
                  <td className="py-1.5 text-right tabular-nums">{a.orders}</td>
                  <td className="py-1.5 text-right tabular-nums">{a.invoices}</td>
                  <td className="py-1.5 text-right tabular-nums">{a.wallet_txns}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pt-3">
            <Button variant="destructive" onClick={() => setCleanupOpen(true)} data-testid="demo-cleanup-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1"/>Remove all demo data
            </Button>
          </div>
        </Card>
      )}

      <DemoCleanupDialog open={cleanupOpen} onClose={() => setCleanupOpen(false)} onDone={load}/>
    </div>
  );
}


function DemoCleanupDialog({ open, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 3) { toast.error("Reason required (min 3 chars)"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/demo-cleanup", { reason: reason.trim() });
      const totals = Object.entries(r.data.deleted).map(([email, s]) =>
        `${email}: ${Object.entries(s).map(([k,v]) => `${v} ${k}`).join(" · ")}`,
      ).join("\n");
      toast.success(`Demo data removed · ${totals}`, { duration: 6000 });
      onDone(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="demo-cleanup-dialog">
        <DialogHeader>
          <DialogTitle className="text-rose-700">Remove all demo data</DialogTitle>
          <DialogDescription>
            This will <strong>permanently delete</strong> buyer@apex.com and seller@glowco.com along with every order, invoice, wallet, payment method, and session attached to them. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Reason <span className="text-rose-600">*</span></Label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Confirm reason (audit-logged)" data-testid="demo-cleanup-reason"/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={busy || reason.trim().length < 3}
                  data-testid="demo-cleanup-submit">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}
            <Trash2 className="w-3.5 h-3.5 mr-1"/>Yes, delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PasswordChangeDialog({ open, onClose }) {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (newPw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password changed. Active sessions are unaffected.");
      setOldPw(""); setNewPw(""); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change password");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="pw-dialog">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>Min 8 characters. Active sessions are unaffected — log out and back in to re-establish.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label>Current password</Label>
          <Input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} data-testid="pw-old"/>
          <Label>New password</Label>
          <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="pw-new"/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !oldPw || newPw.length < 8} data-testid="pw-submit">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Change
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Security tab ──────────────────────────────────────────────────── */
function SecurityTab({ whoami, onRefresh }) {
  const tf = whoami.twofa || {};
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const regen = async () => {
    const reason = window.prompt("Reason for regenerating recovery codes (min 3 chars):");
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/settings/recovery-codes/regenerate", { reason });
      setCodes(r.data.codes);
      toast.success(`Generated ${r.data.codes.length} new recovery codes`);
    } catch (e) {
      toast.error("Failed to regenerate codes");
    } finally { setBusy(false); }
  };

  const trustDevice = async () => {
    try {
      await api.post("/admin/settings/trusted-devices/trust-current");
      toast.success("This device is now trusted.");
      onRefresh();
    } catch (e) {
      toast.error("Failed to trust device");
    }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <Card title="Two-factor authentication" hint="TOTP (authenticator app) is the gold standard for admin accounts.">
        <Row label="TOTP (authenticator app)"
          value={<Status ok={tf.totp_enabled} okLabel="Enabled" badLabel="Not set up"/>}/>
        <Row label="SMS 2FA"
          value={<Status ok={tf.sms_enabled} okLabel="Enabled" badLabel="Not configured"/>}/>
        <Row label="Recovery codes remaining"
          value={<span className="mono">{whoami.recovery_codes_unused} / 10</span>}/>
        <div className="pt-3 flex gap-2">
          <Button variant="outline" onClick={regen} disabled={busy} data-testid="regen-codes">
            <RefreshCw className="w-3.5 h-3.5 mr-1"/>Regenerate recovery codes
          </Button>
        </div>
        {!tf.totp_enabled && (
          <div className="mt-4 pt-4 border-t border-border" data-testid="enroll-totp">
            <div className="text-[12.5px] font-semibold mb-2 flex items-center gap-1.5">
              <Fingerprint className="w-3.5 h-3.5"/>Enrol TOTP authenticator
            </div>
            <div className="text-[11px] text-foreground/55 mb-3">
              Use Google Authenticator, 1Password, Authy or any standards-compliant TOTP app. Scan the QR or paste the secret into your authenticator.
            </div>
            <TwoFactorPanel/>
          </div>
        )}
      </Card>

      {codes && (
        <Card title="Your new recovery codes" hint="Save these now — they're shown only once.">
          <div className="grid grid-cols-2 gap-1.5 mono text-[12px]" data-testid="recovery-codes-list">
            {codes.map(c => (
              <div key={c} className="bg-foreground/5 border border-border rounded px-2 py-1 flex items-center justify-between">
                <span>{c}</span>
                <button onClick={() => { navigator.clipboard?.writeText(c); toast.success("Copied"); }} className="text-foreground/55 hover:text-foreground">
                  <Copy className="w-3 h-3"/>
                </button>
              </div>
            ))}
          </div>
          <div className="pt-3 flex justify-between items-center">
            <div className="text-[10.5px] text-rose-700">⚠ Store these in a password manager. They cannot be retrieved later.</div>
            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard?.writeText(codes.join("\n")); toast.success("All codes copied"); }}>
              <Copy className="w-3 h-3 mr-1"/>Copy all
            </Button>
          </div>
        </Card>
      )}

      <TrustedDevicesCard onTrustCurrent={trustDevice}/>
    </div>
  );
}


function TrustedDevicesCard({ onTrustCurrent }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    try {
      const r = await api.get("/admin/settings/trusted-devices");
      setDevices(r.data.rows || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this trusted device?")) return;
    try {
      await api.delete(`/admin/settings/trusted-devices/${id}`);
      toast.success("Device removed.");
      refresh();
    } catch (e) { toast.error("Failed to remove device"); }
  };

  return (
    <Card title="Trusted devices" hint="A new device requires email re-verification before signing in.">
      {loading ? <Loader2 className="w-3 h-3 animate-spin text-foreground/40"/> :
       devices.length === 0 ? (
         <div className="text-[12px] text-foreground/55 py-2">No trusted devices yet.</div>
       ) : (
         <ul className="divide-y divide-border" data-testid="trusted-device-list">
           {devices.map(d => (
             <li key={d.id} className="py-2 flex items-center gap-3 text-[12px]" data-testid={`trusted-device-${d.id}`}>
               <Fingerprint className="w-3.5 h-3.5 text-foreground/55"/>
               <div className="flex-1 min-w-0">
                 <div className="font-medium truncate">{d.user_agent || "Device"}</div>
                 <div className="text-[10.5px] mono text-foreground/55">{d.ip} · approved {new Date(d.approved_at).toLocaleDateString()}</div>
               </div>
               <Button size="sm" variant="outline" className="border-rose-300 text-rose-700" onClick={() => remove(d.id)} data-testid={`trusted-device-remove-${d.id}`}>
                 <Trash2 className="w-3 h-3"/>
               </Button>
             </li>
           ))}
         </ul>
       )}
      <div className="pt-3">
        <Button variant="outline" size="sm" onClick={onTrustCurrent} data-testid="trust-current-device">
          <Plus className="w-3 h-3 mr-1"/>Trust current device
        </Button>
      </div>
    </Card>
  );
}


/* ─── Hardening tab ─────────────────────────────────────────────────── */
function HardeningTab({ cfg, onRefresh }) {
  const [local, setLocal] = useState(cfg);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [ipText, setIpText] = useState((cfg.ip_allowlist || []).join("\n"));

  const dirty = JSON.stringify(local) !== JSON.stringify(cfg) ||
                ipText !== (cfg.ip_allowlist || []).join("\n");

  const save = async () => {
    if (!reason || reason.trim().length < 3) {
      toast.error("Reason required (min 3 chars) — audit-logged");
      return;
    }
    const ip_allowlist = ipText.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
    setBusy(true);
    try {
      await api.patch("/admin/settings/config", {
        ...local, ip_allowlist, reason: reason.trim(),
      });
      toast.success("Hardening config saved · audit-logged.");
      setReason("");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <Card title="Admin authentication" hint="Industry standard: enforce 2FA for all admin accounts.">
        <ToggleRow label="Force 2FA on admin login"
          hint="If on, admins must enrol TOTP or SMS before signing in. Phishing-resistant."
          checked={!!local.enforce_2fa}
          onChange={(v) => setLocal({...local, enforce_2fa: v})}
          testid="toggle-enforce-2fa"/>
        <ToggleRow label="Magic-link sign-in"
          hint="Allows passwordless admin login via email. Useful as a recovery path."
          checked={!!local.magic_link_enabled}
          onChange={(v) => setLocal({...local, magic_link_enabled: v})}
          testid="toggle-magic-link"/>
        <ToggleRow label="Device-trust requirement"
          hint="When ON, sign-in from an untrusted device requires email re-verification."
          checked={!!local.device_trust_enabled}
          onChange={(v) => setLocal({...local, device_trust_enabled: v})}
          testid="toggle-device-trust"/>
      </Card>

      <Card title="IP allowlist"
        hint="When enabled, admin endpoints reject requests from IPs not in this list. Supports single IPs (1.2.3.4) and CIDR ranges (10.0.0.0/24). Env-only ADMIN_IP_ALLOWLIST always takes effect even when the toggle is off.">
        <ToggleRow label="Enforce IP allowlist"
          hint={local.ip_allowlist_enabled
            ? "Active — only allowlisted IPs can reach /api/admin/* endpoints."
            : "Inactive — admin endpoints are reachable from any IP."}
          checked={!!local.ip_allowlist_enabled}
          onChange={(v) => setLocal({...local, ip_allowlist_enabled: v})}
          testid="toggle-ip-allowlist"/>
        <div className="pt-2">
          <Label htmlFor="ip-list">Allowlist (one per line · CIDR or single IP)</Label>
          <Textarea id="ip-list" rows={4} className="mono text-[12px]"
            placeholder="203.0.113.0/24&#10;198.51.100.42"
            value={ipText}
            onChange={(e) => setIpText(e.target.value)}
            data-testid="ip-allowlist-text"/>
        </div>
      </Card>

      <Card title="Session timeouts" hint="Industry standard: 15-min idle · 12-hour max (Okta, Microsoft Entra).">
        <NumberRow label="Idle timeout (minutes)"
          hint="No activity for longer than this → admin is auto-signed-out."
          value={local.idle_ttl_minutes}
          onChange={(v) => setLocal({...local, idle_ttl_minutes: Number(v) || 15})}
          testid="idle-ttl"/>
        <NumberRow label="Max session lifetime (hours)"
          hint="Total session lifetime cap regardless of activity. Forces a re-auth after this."
          value={local.max_ttl_hours}
          onChange={(v) => setLocal({...local, max_ttl_hours: Number(v) || 12})}
          testid="max-ttl"/>
      </Card>

      {dirty && (
        <Card title="Confirm changes" hint="Every hardening change is audit-logged with a mandatory reason.">
          <Textarea rows={2} placeholder="Why are you making this change? (min 3 chars · audit-logged)"
            value={reason} onChange={(e) => setReason(e.target.value)} data-testid="hardening-reason"/>
          <div className="pt-2 flex gap-2 items-center">
            <Button onClick={save} disabled={busy || reason.trim().length < 3} data-testid="hardening-save">
              {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}
              <CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Save & audit
            </Button>
            <Button variant="ghost" onClick={() => { setLocal(cfg); setIpText((cfg.ip_allowlist || []).join("\n")); setReason(""); }}>
              Discard
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}


/* ─── Anomalies tab ─────────────────────────────────────────────────── */
function AnomaliesTab({ cfg, onRefresh }) {
  const [thr, setThr] = useState(cfg.anomaly_thresholds || {});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(thr) !== JSON.stringify(cfg.anomaly_thresholds || {});

  const save = async () => {
    if (!reason || reason.trim().length < 3) { toast.error("Reason required"); return; }
    setBusy(true);
    try {
      await api.patch("/admin/settings/config", {
        anomaly_thresholds: thr, reason: reason.trim(),
      });
      toast.success("Anomaly thresholds saved · audit-logged.");
      setReason("");
      onRefresh();
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const FIELDS = [
    ["velocity_n",      "Velocity spike threshold", "Actor doing >N overrides per hour"],
    ["impact_usd",      "$ Impact threshold",       "Actor cumulative impact > $X in 24h"],
    ["same_target_n",   "Same-target spam threshold","Actor > N actions on same user in 1h"],
    ["revert_n",        "Revert spike threshold",   "Actor doing > N reverts per hour"],
    ["off_hours_n",     "Off-hours threshold",      "Actor > N actions during off-hours (22:00–06:00 UTC)"],
    ["pending_stale_h", "Stale pending (hours)",    "Pending-approval rows aging > N hours"],
    ["revert_ratio_pct","High revert ratio (%)",    "Actor with > N% of last 50 overrides reverted"],
  ];

  return (
    <div className="space-y-4 max-w-3xl">
      <Card title="Anomaly detection thresholds" hint="Live on /admin/overrides Anomaly Panel. Changing these takes effect on the next scan.">
        {FIELDS.map(([k, label, hint]) => (
          <NumberRow key={k} label={label} hint={hint}
            value={thr[k]}
            onChange={(v) => setThr({...thr, [k]: Number(v) || 0})}
            testid={`anom-${k}`}/>
        ))}
      </Card>

      {dirty && (
        <Card title="Confirm changes" hint="Threshold changes are audit-logged.">
          <Textarea rows={2} placeholder="Reason (min 3 chars)"
            value={reason} onChange={(e) => setReason(e.target.value)} data-testid="anom-reason"/>
          <div className="pt-2 flex gap-2">
            <Button onClick={save} disabled={busy || reason.trim().length < 3} data-testid="anom-save">
              {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Save & audit
            </Button>
            <Button variant="ghost" onClick={() => { setThr(cfg.anomaly_thresholds || {}); setReason(""); }}>Discard</Button>
          </div>
        </Card>
      )}
    </div>
  );
}


/* ─── User audit chain tab (CC-2) ───────────────────────────────────── */
function UserAuditTab() {
  const [status, setStatus] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [logins, setLogins] = useState({ rows: [], summary: {} });
  const [busy, setBusy] = useState(false);
  const [filterRole, setFilterRole] = useState("");
  const [filterSuccess, setFilterSuccess] = useState("");

  const refresh = async () => {
    setBusy(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/user-audit-chain/status"),
        api.get("/admin/login-audit/non-admin", {
          params: {
            since_days: 7,
            ...(filterRole ? { role: filterRole } : {}),
            ...(filterSuccess ? { success: filterSuccess } : {}),
          },
        }),
      ]);
      setStatus(s.data);
      setLogins(l.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setBusy(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filterRole, filterSuccess]);

  const verifyNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/user-audit-chain/verify");
      setVerifyResult(r.data);
      if (r.data.valid) toast.success(`Chain valid · ${r.data.verified_rows} rows in ${r.data.duration_ms}ms`);
      else toast.error(`Chain BROKEN at position #${r.data.broken_at}`);
      refresh();
    } catch (e) { toast.error("Verify failed"); }
    finally { setBusy(false); }
  };

  const repairNow = async () => {
    if (!window.confirm("Re-stamp the user audit chain forward from position 1? Idempotent and audit-logged.")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/user-audit-chain/repair");
      toast.success(`Repaired ${r.data.repaired} row(s); walked ${r.data.walked}.`);
      setVerifyResult(null);
      refresh();
    } catch (e) { toast.error("Repair failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="user-audit-tab">
      <Card title="User audit chain integrity"
            hint="Hash-chained tamper-evident log of buyer/seller actions (parallel to admin chain). SOC2 CC7.2.">
        {status ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="chip bg-foreground/5 border-foreground/10" data-testid="user-chain-head-position">
                Head position: <span className="mono ml-1">#{status.head_position ?? 0}</span>
              </span>
              <span className="chip bg-foreground/5 border-foreground/10" data-testid="user-chain-stamped-rows">
                Stamped rows: <span className="mono ml-1">{status.stamped_rows}</span>
              </span>
              {status.last_verified_at && (
                <span className={`chip ${status.last_verified_ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
                  {status.last_verified_ok ? "Verified" : "Broken"} · last @ {new Date(status.last_verified_at).toLocaleString()}
                </span>
              )}
            </div>
            <div className="rounded-md bg-foreground/3 px-2.5 py-1.5 text-[11px] mono text-foreground/65 break-all"
                 data-testid="user-chain-head-hash">
              head_hash: {status.head_hash}
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={verifyNow} disabled={busy} data-testid="user-chain-verify-btn">
                {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Verify chain now
              </Button>
              {verifyResult && !verifyResult.valid && (
                <Button size="sm" variant="destructive" onClick={repairNow} disabled={busy}
                  data-testid="user-chain-repair-btn">Repair chain</Button>
              )}
            </div>
            {verifyResult && (
              <div className={`text-[12px] rounded-md p-3 ${verifyResult.valid ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}
                   data-testid="user-chain-verify-result">
                {verifyResult.valid ? (
                  <span className="text-emerald-900">Verified {verifyResult.verified_rows} rows · chain intact end-to-end ({verifyResult.duration_ms}ms)</span>
                ) : (
                  <span className="text-rose-900">Chain broken at position #{verifyResult.broken_at}. Click Repair to re-stamp forward.</span>
                )}
              </div>
            )}
          </div>
        ) : <Loader2 className="w-4 h-4 animate-spin"/>}
      </Card>

      <Card title="Buyer / seller login forensics (last 7 days)"
            hint="Every non-admin /auth/login attempt with IP + UA. Useful for credential-stuffing & compromised-account triage.">
        <div className="flex items-center gap-3 flex-wrap" data-testid="non-admin-audit-summary">
          <span className="chip bg-emerald-50 border-emerald-200 text-emerald-700">
            {logins.summary?.success ?? 0} success
          </span>
          <span className="chip bg-rose-50 border-rose-200 text-rose-700">
            {logins.summary?.failed ?? 0} failed
          </span>
          <span className="chip bg-sky-50 border-sky-200 text-sky-700">
            {logins.summary?.buyer ?? 0} buyer
          </span>
          <span className="chip bg-violet-50 border-violet-200 text-violet-700">
            {logins.summary?.seller ?? 0} seller
          </span>
          <div className="ml-auto flex gap-1.5">
            <select value={filterRole} onChange={e => setFilterRole(e.target.value)}
              className="h-7 px-2 rounded-md border bg-background text-[11.5px]"
              data-testid="non-admin-audit-role-filter">
              <option value="">All roles</option>
              <option value="buyer">Buyer</option>
              <option value="seller">Seller</option>
            </select>
            <select value={filterSuccess} onChange={e => setFilterSuccess(e.target.value)}
              className="h-7 px-2 rounded-md border bg-background text-[11.5px]"
              data-testid="non-admin-audit-success-filter">
              <option value="">Any outcome</option>
              <option value="true">Success</option>
              <option value="false">Failed</option>
            </select>
          </div>
        </div>
        <div className="rounded-xl border bg-background overflow-x-auto mt-3">
          <table className="w-full text-[12px]" data-testid="non-admin-audit-table">
            <thead className="text-left text-[10px] uppercase tracking-wider text-foreground/55 border-b">
              <tr>
                <th className="px-3 py-2 font-medium">When</th>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Outcome</th>
                <th className="px-3 py-2 font-medium">IP</th>
                <th className="px-3 py-2 font-medium">UA</th>
              </tr>
            </thead>
            <tbody>
              {(logins.rows || []).map(r => (
                <tr key={r.id} className="border-b last:border-0" data-testid={`non-admin-audit-row-${r.id}`}>
                  <td className="px-3 py-2 mono whitespace-nowrap text-foreground/65">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">{r.email}</td>
                  <td className="px-3 py-2">
                    <span className={`chip text-[10.5px] ${
                      r.role === "buyer" ? "bg-sky-50 border-sky-200 text-sky-800"
                                            : "bg-violet-50 border-violet-200 text-violet-800"
                    }`}>{r.role}</span>
                  </td>
                  <td className="px-3 py-2">
                    {r.success
                      ? <span className="chip bg-emerald-50 border-emerald-200 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-0.5 inline"/>OK</span>
                      : <span className="chip bg-rose-50 border-rose-200 text-rose-700"><XCircle className="w-3 h-3 mr-0.5 inline"/>{r.reason || "fail"}</span>}
                  </td>
                  <td className="px-3 py-2 mono">{r.ip}</td>
                  <td className="px-3 py-2 text-[10.5px] text-foreground/55 max-w-xs truncate"
                       title={r.user_agent}>{r.user_agent}</td>
                </tr>
              ))}
              {(!logins.rows || logins.rows.length === 0) && (
                <tr><td colSpan="6" className="px-3 py-8 text-center text-foreground/55">No non-admin logins in this window.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}


/* ─── Audit tab ─────────────────────────────────────────────────────── */
function AuditTab() {
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const refresh = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { success: filter === "success" ? "true" : "false" };
      const r = await api.get("/admin/settings/login-audit", { params });
      setData(r.data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3" data-testid="audit-summary">
        <span className="chip bg-emerald-50 border-emerald-200 text-emerald-700">
          {data.summary?.last_24h_success ?? 0} successful · 24h
        </span>
        <span className="chip bg-rose-50 border-rose-200 text-rose-700">
          {data.summary?.last_24h_fail ?? 0} failed · 24h
        </span>
        <div className="ml-auto flex gap-1">
          {["all", "success", "fail"].map(f => (
            <button key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-[11px] rounded-full border ${filter === f ? "bg-foreground text-background border-foreground" : "bg-background border-border text-foreground/65"}`}
              data-testid={`audit-filter-${f}`}>
              {f}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-foreground/40"/>
      ) : (
        <div className="rounded-xl border bg-background overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="text-left text-[10px] uppercase tracking-wider text-foreground/55 border-b">
              <tr>
                <th className="px-3 py-2 font-medium">When</th>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Method</th>
                <th className="px-3 py-2 font-medium">IP</th>
                <th className="px-3 py-2 font-medium">User-agent</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.id} className="border-b last:border-0" data-testid={`audit-row-${r.id}`}>
                  <td className="px-3 py-2 mono whitespace-nowrap text-foreground/65">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-3 py-2">{r.email}</td>
                  <td className="px-3 py-2">
                    {r.success
                      ? <span className="chip bg-emerald-50 border-emerald-200 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-0.5 inline"/>OK</span>
                      : <span className="chip bg-rose-50 border-rose-200 text-rose-700"><XCircle className="w-3 h-3 mr-0.5 inline"/>{r.reason || "fail"}</span>
                    }
                  </td>
                  <td className="px-3 py-2 mono text-foreground/65">{r.method}</td>
                  <td className="px-3 py-2 mono">{r.ip}</td>
                  <td className="px-3 py-2 text-[10.5px] text-foreground/55 max-w-xs truncate" title={r.user_agent}>{r.user_agent}</td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr><td colSpan="6" className="px-3 py-8 text-center text-foreground/55">No login attempts logged yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/* ─── Reusable bits ─────────────────────────────────────────────────── */

function Card({ title, hint, children }) {
  return (
    <div className="rounded-2xl border bg-background p-4 space-y-2">
      <div>
        <h3 className="text-[13px] font-semibold">{title}</h3>
        {hint && <p className="text-[11px] text-foreground/55 mt-0.5">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function Kv({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-3 py-1.5 border-b border-border/40 last:border-b-0">
      <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/50 w-32 shrink-0">{label}</div>
      <div className={`text-[13px] ${mono ? "mono" : ""} text-foreground/85 flex-1 break-words`}>{value}</div>
    </div>
  );
}

function Row({ label, value }) { return <Kv label={label} value={value}/>; }

function ToggleRow({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium">{label}</div>
        {hint && <div className="text-[11px] text-foreground/55 mt-0.5">{hint}</div>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid}/>
    </div>
  );
}

function NumberRow({ label, hint, value, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium">{label}</div>
        {hint && <div className="text-[11px] text-foreground/55 mt-0.5">{hint}</div>}
      </div>
      <Input type="number" className="w-28 text-right mono" value={value ?? ""}
        onChange={(e) => onChange(e.target.value)} data-testid={testid}/>
    </div>
  );
}

function Status({ ok, okLabel, badLabel }) {
  return ok
    ? <span className="chip bg-emerald-50 border-emerald-200 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-0.5 inline"/>{okLabel}</span>
    : <span className="chip bg-amber-50 border-amber-200 text-amber-700"><Clock className="w-3 h-3 mr-0.5 inline"/>{badLabel}</span>;
}
